import { LogOut, Menu, ChevronDown, Settings, Sparkles } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';

import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { CDN_URL } from '@/config/cdn';


/**
 * Member portal header
 * Desktop: Full header with logo, portal title, divider, welcome message, student selector, sign out
 * Mobile: Compact with logo, student name, hamburger menu
 */
const MemberHeader = ({
  student,
  students = [],
  customerName,
  onSelectStudent,
  onMenuClick,
  onLogout,
  isImpersonation = false,
  isStudent = false,
  onOpenPreferences = () => {},
  onOpenWhatsNew = () => {},
  hasUnreadUpdates = false,
  onAppearanceChange = () => {},
  appearanceSaving = false,
}) => {
  const hasMultipleStudents = students.length > 1;
  const displayName = isStudent
    ? student?.name || 'Student'
    : customerName || 'Member';

  // Header always sticks at top:0 - banner with higher z-index sits on top
  const headerTop = '0';

  return (
    <header
      className="member-header z-[60]"
      style={{ height: '64px', top: headerTop, position: 'sticky' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Left section: Logo + Title + Divider + Welcome */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Logo with gold ring glow */}
            <div className="relative group flex-shrink-0">
              <div className="absolute -inset-1 rounded-full bg-[#C9A227] blur-md opacity-50 group-hover:opacity-70 transition-opacity duration-500"></div>
              <img
                src={`${CDN_URL}/images/owaza-logo.jpg`}
                alt="Owaza Martial Arts"
                className="relative h-10 w-10 md:h-11 md:w-11 object-cover rounded-full shadow-xl ring-2 ring-[#C9A227]"
              />
            </div>

            {/* Portal Title - hidden on mobile */}
            <div className="hidden md:block">
              <h1 className="text-lg font-outfit font-bold text-stone-800 dark:text-white tracking-tight">
                {isStudent ? 'Student Portal' : 'Member Portal'}
              </h1>
              <p className="text-xs text-[#C9A227] font-medium">Owaza Martial Arts</p>
            </div>

            {/* Vertical Divider - hidden on mobile */}
            <div className="hidden md:block h-10 w-px bg-[#C9A227]/40" />

            {/* Welcome Message + Student Selector - hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <div>
                <p className="text-sm text-stone-700 dark:text-white font-outfit">
                  Welcome back, <span className="font-semibold text-[#C9A227]">{displayName}</span>
                </p>
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  {isStudent
                    ? 'View your training progress and resources'
                    : 'Track your progress and manage your membership'}
                </p>
              </div>

              {/* Student Selector Dropdown (if multiple students) */}
              {hasMultipleStudents && (
                <Select value={student?.id} onValueChange={onSelectStudent}>
                  <SelectTrigger className="w-auto min-w-[120px] bg-stone-100 dark:bg-white/10 border-[#C9A227]/30 text-stone-700 dark:text-white hover:bg-stone-200 dark:hover:bg-white/20 focus:ring-[#C9A227]/50">
                    <div className="flex items-center gap-2">
                      <span className="font-outfit text-sm truncate">
                        {student?.name || 'Select'}
                      </span>
                      <ChevronDown className="h-4 w-4 text-[#C9A227]" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#1A1814] border-[#C9A227]/30">
                    {students.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        className="text-stone-700 dark:text-white hover:bg-stone-100 dark:hover:bg-white/10 focus:bg-stone-100 dark:focus:bg-white/10 focus:text-stone-800 dark:focus:text-white cursor-pointer"
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Mobile: Just show student name */}
            <div className="md:hidden">
              {hasMultipleStudents ? (
                <Select value={student?.id} onValueChange={onSelectStudent}>
                  <SelectTrigger className="w-auto bg-transparent border-none text-stone-700 dark:text-white hover:bg-stone-100 dark:hover:bg-white/10 focus:ring-0 focus:ring-offset-0 p-0">
                    <div className="flex items-center gap-1">
                      <span className="font-outfit font-semibold text-sm truncate max-w-[150px]">
                        {student?.name || 'Select Student'}
                      </span>
                      <ChevronDown className="h-4 w-4 text-[#C9A227]" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#1A1814] border-[#C9A227]/30">
                    {students.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        className="text-stone-700 dark:text-white hover:bg-stone-100 dark:hover:bg-white/10 focus:bg-stone-100 dark:focus:bg-white/10 focus:text-stone-800 dark:focus:text-white cursor-pointer"
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="font-outfit font-semibold text-stone-800 dark:text-white text-sm truncate max-w-[150px]">
                  {student?.name || 'Member Portal'}
                </span>
              )}
            </div>
          </div>

          {/* Right section: Sign Out (desktop) or Menu (mobile) */}
          <div className="flex items-center">
            {/* Mobile hamburger menu */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden p-2 text-[#C9A227] hover:bg-stone-100 dark:hover:bg-white/10 hover:text-[#C9A227]"
              onClick={onMenuClick}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Desktop: Theme toggle and sign out */}
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => onOpenWhatsNew(event.currentTarget)}
                aria-label={`What's New${hasUnreadUpdates ? ', unread updates' : ''}`}
                className="relative text-stone-700 dark:text-amber-100"
              >
                <Sparkles className="h-4 w-4" />
                {hasUnreadUpdates && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#9B1B30]" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => onOpenPreferences(event.currentTarget)}
                aria-label="Open personal preferences"
                className="text-stone-700 dark:text-amber-100"
                disabled={appearanceSaving}
              >
                <Settings className="h-4 w-4" />
              </Button>
              <ThemeToggle
                variant="member"
                onThemeChange={onAppearanceChange}
                disabled={appearanceSaving}
              />
              <Button
                variant="memberOutline"
                size="sm"
                onClick={onLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MemberHeader;
